import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Users, BookOpen, AlertTriangle, TrendingUp, Building2, Clock, Inbox, Activity, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-border rounded-xl p-4" data-testid={`stat-${label}`}>
    <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.15em] uppercase text-muted-foreground">
      <Icon size={12} strokeWidth={1.5} /> {label}
    </div>
    <div className="font-display text-3xl font-semibold mt-1">{value}</div>
  </div>
);

const Panel = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-border rounded-xl p-5">
    <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
      <Icon size={15} strokeWidth={1.8} className="text-brand" /> {title}
    </h3>
    {children}
  </div>
);

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const brand = user?.theme?.primary || "#500000";

  const loadStatus = () => {
    setStatusBusy(true);
    api.get("/admin/system-status").then((r) => setStatus(r.data)).catch(() => {}).finally(() => setStatusBusy(false));
  };
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); loadStatus(); }, []);

  if (!data) return <AppShell><div className="p-8 mono text-xs uppercase tracking-widest text-brand animate-pulse">Crunching campus data…</div></AppShell>;

  const strain = data.schedule_strain || {};
  const strainTotal = (strain.ok || 0) + (strain.tight || 0) + (strain.impossible || 0);
  const days = Object.entries(data.days_on_campus || {}).map(([k, v]) => ({ label: `${k} day${k > 1 ? "s" : ""}`, count: v }));

  return (
    <AppShell>
      <div className="px-4 lg:px-6 py-5" data-testid="admin-analytics">
        <div className="mb-5 pr-16 lg:pr-20">
          <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Admin · {data.school.name}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Campus Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Anonymized, aggregate insight — no individual student is identifiable.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
          <Stat icon={Users} label="Students" value={data.totals.students} />
          <Stat icon={BookOpen} label="Classes tracked" value={data.totals.classes} />
          <Stat icon={TrendingUp} label="With schedules" value={data.totals.with_schedule} />
        </div>

        {status && (
          <div className="bg-white border border-border rounded-xl p-5 mb-4" data-testid="system-status">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Activity size={15} strokeWidth={1.8} className="text-brand" /> System status
                <span className="mono text-[11px] font-bold rounded px-2 py-0.5" style={{
                  background: status.status === "ok" ? "#16a34a15" : status.status === "degraded" ? "#F59E0B15" : "#dc262615",
                  color: status.status === "ok" ? "#16a34a" : status.status === "degraded" ? "#F59E0B" : "#dc2626",
                }}>{status.status === "ok" ? "ALL SYSTEMS GO" : status.status.toUpperCase()}</span>
              </h3>
              <button data-testid="refresh-status" onClick={loadStatus} disabled={statusBusy}
                className="p-1.5 rounded-md hover:bg-secondary transition-colors" title="Re-check now">
                <RefreshCw size={14} strokeWidth={1.8} className={statusBusy ? "animate-spin" : ""} />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {status.services.map((s) => (
                <div key={s.name} data-testid={`svc-${s.name}`} className="flex items-center gap-2.5 rounded-lg border border-border px-3 py-2">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${s.ok ? "bg-[#16a34a]" : "bg-[#dc2626]"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{s.detail}</div>
                  </div>
                  <span className="mono text-[11px] text-muted-foreground shrink-0">{s.ms}ms</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Uptime monitors can hit the public <code className="bg-secondary px-1 rounded">/api/health</code> endpoint.</p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-4">
          <Panel title="Busiest building corridors" icon={Building2}>
            {data.busiest_corridors.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not enough schedule data yet.</p>
            ) : (
              <div className="space-y-2" data-testid="corridors-list">
                {data.busiest_corridors.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-sm font-semibold w-40 truncate">{c.from} → {c.to}</span>
                    <div className="flex-1 h-5 bg-secondary rounded overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${(c.count / data.busiest_corridors[0].count) * 100}%`, background: brand }} />
                    </div>
                    <span className="mono text-xs font-bold w-6 text-right">{c.count}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Schedule strain" icon={AlertTriangle}>
            <div className="flex gap-2 mb-3">
              {[
                { k: "impossible", label: "Impossible", color: "#dc2626" },
                { k: "tight", label: "Tight", color: "#F59E0B" },
                { k: "ok", label: "Comfortable", color: "#16a34a" },
              ].map((s) => (
                <div key={s.k} className="flex-1 rounded-lg p-3 text-center" style={{ background: `${s.color}15` }} data-testid={`strain-${s.k}`}>
                  <div className="font-display text-2xl font-semibold" style={{ color: s.color }}>{strain[s.k] || 0}</div>
                  <div className="text-[11px] font-semibold text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {strainTotal > 0 && strain.impossible > 0
                ? `${strain.impossible} transitions are physically impossible on time — flag these to advising & registrar.`
                : "Transitions between back-to-back classes across all schedules."}
            </p>
          </Panel>

          <Panel title="Peak demand by hour (class starts)" icon={Clock}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.peak_hours}>
                <XAxis dataKey="hour" tickFormatter={(h) => `${h}`} fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} width={24} />
                <Tooltip labelFormatter={(h) => `${h}:00`} cursor={{ fill: "#00000008" }} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {data.peak_hours.map((e, i) => <Cell key={i} fill={brand} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground mt-1">Use for shuttle frequency & staffing decisions.</p>
          </Panel>

          <Panel title="Days on campus per student" icon={Users}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={days}>
                <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} width={24} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#00000008" }} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {days.map((e, i) => <Cell key={i} fill={brand} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          {data.top_requests.length > 0 && (
            <Panel title="Top campus requests" icon={Inbox}>
              <div className="space-y-2" data-testid="top-requests">
                {data.top_requests.map((r, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm font-semibold truncate">{r.school_name}</span>
                    <span className="mono text-xs font-bold bg-brand/10 text-brand rounded px-2 py-0.5">{r.count}</span>
                  </div>
                ))}
              </div>
            </Panel>
          )}
        </div>
      </div>
    </AppShell>
  );
}
